<template>
  <form v-if="shouldRender" ref="formElement" class="andale-smart-stock-notifications-signup-form ssn-signup-form"
    :class="[`ssn-signup-form--${uiVariant}`, `ssn-signup-form--layout-${uiLayout}`]"
    :style="formInlineStyle"
    @submit.prevent="doSignup">
    <template v-if="isSingleBisCompact">
      <p class="ssn-single-bis-heading" data-ssn-part="single-bis-heading">
        {{ localizations.getNotified }}
      </p>
      <p class="ssn-single-bis-description" data-ssn-part="single-bis-description">
        {{ localizations.notifyWhenBackInStock }}
      </p>
      <div class="ssn-single-bis-controls" :class="{ 'ssn-single-bis-controls--logged-in': isLoggedIn }" data-ssn-part="single-bis-controls">
        <div v-if="!isLoggedIn" class="ssn-email-field" data-ssn-part="email-field">
          <label class="ssn-field-label" data-ssn-part="email-label" for="ssn-signup-email">{{ localizations.enterYourEmail }}</label>
          <input id="ssn-signup-email" v-model="email" type="email" class="ssn-text-input" data-ssn-part="email-input"
            placeholder="you@example.com"
            autocomplete="email" required />
        </div>

        <div class="andale-smart-stock-notifications-signup-cta" data-ssn-part="cta">
          <button type="submit" :class="submitButtonClass" data-ssn-part="submit-button" :disabled="!canSubmit || subscribing">
            <span>{{ localizations.notifyMe }}</span>
            <CircularProgress v-if="subscribing" class="px-1 ml-2" size="25px" :animation-duration="'0.6s'"
              slim />
          </button>
        </div>
      </div>
    </template>
    <template v-else>
      <!-- Email Field (only if not logged in) -->
      <div v-if="!isLoggedIn" class="ssn-email-field" data-ssn-part="email-field">
        <label class="ssn-field-label" data-ssn-part="email-label" for="ssn-signup-email">{{ localizations.enterYourEmail }}</label>
        <input id="ssn-signup-email" v-model="email" type="email" class="ssn-text-input" data-ssn-part="email-input"
          placeholder="you@example.com"
          autocomplete="email" required />
      </div>

      <div class="signup-options">
        <span class="ssn-signup-form-title" data-ssn-part="title">
          {{ localizations.getNotified }}
        </span>
        <!-- Back-in-Stock Signup Section -->
        <BackInStockSignup v-if="shouldRenderBackInStock" :product-id="productId"
          v-model="isSubscribedToBackInStock" :uuid="backInStockSubscription?.uuid"
          class="andale-smart-stock-notifications-a-subscription-option" />

        <!-- Price-Drop Signup Section -->
        <PriceDropSignup v-if="shouldRenderPriceDrop" v-model="priceDropSubscription" :uuid="priceDropSubscription?.uuid"
          :product-id="productId" class="andale-smart-stock-notifications-a-subscription-option" />
      </div>

      <div class="andale-smart-stock-notifications-signup-cta" data-ssn-part="cta">
        <!-- CTA Button -->
        <button type="submit" :class="submitButtonClass" data-ssn-part="submit-button" :disabled="!canSubmit || subscribing">
          <span>{{ localizations.notifyMe }}</span>
          <CircularProgress v-if="subscribing" class="px-1 ml-2" size="25px" :animation-duration="'0.6s'"
            slim />
        </button>
      </div>
    </template>

    <!-- Notification -->
    <div v-if="message" class="ssn-feedback" data-ssn-part="feedback" :class="`ssn-feedback--${messageType}`" role="status"
      aria-live="polite">
      <span>{{ message }}</span>
      <button type="button" class="ssn-feedback__dismiss" @click="dismissMessage"
        :aria-label="localizations.cancel">×</button>
    </div>
  </form>
</template>

<script>
import { ref, reactive, computed, watch, onMounted, nextTick, onBeforeUnmount } from 'vue';

import BackInStockSignup from './BackInStockSignupForm.vue';
import PriceDropSignup from './PriceDropSignupForm.vue';
import CircularProgress from '@sharedJs/components/ui/progress/CircularProgress.vue';
import { useRecaptcha } from '@publicJs/composables/recaptcha/useRecaptcha.js';

const getPublicData = () => (window.andalePluginsStockNotificationsPublic || {
  ajaxUrl: '',
  nonce: '',
  userId: 0,
  userEmail: '',
  isRecaptchaEnabled: false,
  settings: {},
  localizations: {},
});

const andalePluginsStockNotificationsPublic = getPublicData();
const pluginSettings = andalePluginsStockNotificationsPublic.settings || {};
const localizationData = andalePluginsStockNotificationsPublic.localizations || {};
const localizations = {
  userEmail: andalePluginsStockNotificationsPublic.userEmail,
  youAreSignedUpForNotifications: localizationData.youAreSignedUpForNotifications || '',
  notifyWhenBackInStock: localizationData.notifyWhenBackInStock || '',
  notifyWhenPriceDrops: localizationData.notifyWhenPriceDrops || '',
  getNotified: localizationData.getNotified || '',
  unsubscribe: localizationData.unsubscribe || '',
  cancel: localizationData.cancel || 'Cancel',
  enterYourEmail: localizationData.enterYourEmail || 'Enter your email',
  notifyMe: localizationData.notifyMe || 'Notify me',
  youHaveSuccessfullySignedUp: localizationData.youHaveSuccessfullySignedUp || 'You have successfully signed up.',
  youHaveSuccessfullyUnsubscribed: localizationData.youHaveSuccessfullyUnsubscribed || 'You have been successfully unsubscribed!',
  yourNotificationPreferencesHaveBeenUpdated: localizationData.yourNotificationPreferencesHaveBeenUpdated || 'Your notification preferences have been successfully updated!',
  pleaseEnterValidEmail: localizationData.pleaseEnterValidEmail || 'Please enter a valid email address.',
  anErrorOccurred: localizationData.anErrorOccurred || 'An error occurred.',
};
const isRecaptchaEnabled = andalePluginsStockNotificationsPublic.isRecaptchaEnabled;
// @todo: delete
const cookiesOptions = {
  expires: 7, // 7 days
  secure: true,
  sameSite: 'Lax',
};


const andale = andalePluginsStockNotificationsPublic;
const getEmptyBackInStockStatus = () => (
  {
    uuid: null,
    subscribed: true,
  }
);
const getEmptyPriceDropStatus = () => (
  {
    uuid: null,
    subscribed: false,
    targetPrice: null,
    // subscription: { targetPrice: null, }
  }
);
const getEmptySubscriptionStatus = () => (
  {
    backInStock: getEmptyBackInStockStatus(),
    priceDrop: getEmptyPriceDropStatus()
  }
);

const SUCCESS_MESSAGE_AUTO_DISMISS_MS = 45000;

const normalizeLocalizedNumber = (value) => {
  const stringValue = String(value ?? '').trim();

  if (stringValue === '') {
    return '';
  }

  return stringValue.replace(/\s/g, '').replace(',', '.');
};

const formatLocalizedNumber = (value, preserveDecimals = false) => {
  const normalized = normalizeLocalizedNumber(value);

  if (normalized === '') {
    return '';
  }

  const numericValue = Number(normalized);
  if (!Number.isFinite(numericValue)) {
    return String(value ?? '');
  }

  const decimalPlaces = preserveDecimals
    ? Math.min(4, Math.max(0, (normalized.split('.')[1] || '').length))
    : 0;

  try {
    return new Intl.NumberFormat(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(numericValue);
  } catch (error) {
    return numericValue.toFixed(decimalPlaces);
  }
};

const normalizeComparableTargetPrice = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  return String(value).trim();
};

export default {
  name: 'NotificationsSignupForm',
  props: {
    initialProductId: { type: Number, required: true },
    initialProductIsOutOfStock: { type: Boolean, required: true },
    isAutoInjected: { type: Boolean, required: true },
    initialNotificationTypes: { type: Array, default: () => [] },
    initialUiVariant: { type: String, default: '' },
    initialUiLayout: { type: String, default: '' },
    uiCssVars: { type: Object, default: () => ({}) },
    previewMode: { type: Boolean, default: false },
    previewSubscriptionState: { type: Object, default: null },
  },
  components: {
    BackInStockSignup,
    PriceDropSignup,
    CircularProgress,
  },
  setup(props) {
    // const userEmail = ref(localizations.userEmail);

    // region reactive state
    const settings = ref(pluginSettings);
    const isBackInStockEnabled = ref(pluginSettings?.isBackInStockEnabled ? true : false);
    const isBackInStockAutoInject = ref(pluginSettings?.isBackInStockAutoInject ? true : false);
    const isPriceDropEnabled = ref(pluginSettings?.isPriceDropEnabled ? true : false);
    const isPriceDropAutoInject = ref(pluginSettings?.isPriceDropAutoInject ? true : false);

    const email = ref(localizations.userEmail);

    const targetPrice = ref('');
    const productId = ref(props.initialProductId);
    const isProductOutOfStock = ref(props.initialProductIsOutOfStock);
    const subscribtionStatus = reactive(getEmptySubscriptionStatus());
    const originalSubscriptionSnapshot = reactive(getEmptySubscriptionStatus());

    const subscribing = ref(false);
    const uiVariant = ref(props.initialUiVariant || 'inherit');
    const uiLayout = ref(props.initialUiLayout || 'stacked');
    const formElement = ref(null);
    const themeSubmitButtonClasses = ref([]);
    const formInlineStyle = computed(() => props.uiCssVars || {});
    const requestedNotificationTypes = computed(() => {
      const normalized = props.initialNotificationTypes
        .map((value) => String(value || '').trim().toLowerCase())
        .filter((value) => value === 'bis' || value === 'pd');

      return normalized.length > 0 ? normalized : ['bis', 'pd'];
    });
    const allowsBackInStock = computed(() => requestedNotificationTypes.value.includes('bis'));
    const allowsPriceDrop = computed(() => requestedNotificationTypes.value.includes('pd'));

    const { recaptchaToken, recaptchaError, executeRecaptcha } = useRecaptcha();

    const message = ref('');
    const messageType = ref('primary');
    const messageDismissTimeout = ref(null);

    // endregion reactive state

    //#region computed properties

    const shouldRenderBackInStock = computed(() => {
      if (props.previewMode) {
        return allowsBackInStock.value && props.initialProductIsOutOfStock;
      }

      return allowsBackInStock.value && isBackInStockEnabled.value && isProductOutOfStock.value;
    });
    const shouldRenderPriceDrop = computed(() => {
      if (props.previewMode) {
        return allowsPriceDrop.value;
      }

      return allowsPriceDrop.value && isPriceDropEnabled.value;
    });

    const shouldRender = computed(() => {
      return shouldRenderBackInStock.value || shouldRenderPriceDrop.value;
    });

    const isSubscribedToBackInStock = computed({
      get: () => {
        if (shouldRenderBackInStock.value) {
          return subscribtionStatus.backInStock.subscribed;
        }

        return false; // If not enabled, not subscribed
      },
      set: (value) => {
        subscribtionStatus.backInStock.subscribed = value;
      }
    });
    const backInStockSubscription = computed({
      get: () => subscribtionStatus.backInStock,
      set: (value) => {
        subscribtionStatus.backInStock = value;
      }
    });
    const isSubscribedToPriceDrop = computed({
      get: () => {
        if (shouldRenderPriceDrop.value) {
          return subscribtionStatus.priceDrop.subscribed;
        }

        return false; // If not enabled, not subscribed
      }/* ,
      set: (value) => {
        subscribtionStatus.priceDrop.subscribed = value;
      } */
    });
    const priceDropSubscription = computed({
      get: () => subscribtionStatus.priceDrop,
      set: (value) => {
        subscribtionStatus.priceDrop = value;
      }
    });
    const isUnsubscribingFromPriceDrop = computed(() => {
      return subscribtionStatus.priceDrop.uuid && !isSubscribedToPriceDrop.value;
    });
    const isUnsubscribingFromBackInStock = computed(() => {
      return subscribtionStatus.backInStock.uuid && !isSubscribedToBackInStock.value;
    });
    const isUnsubscribing = computed(() => {
      return isUnsubscribingFromPriceDrop.value || isUnsubscribingFromBackInStock.value;
    });

    const isEmailValid = computed(() => {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(email.value);
    });
    const isLoggedIn = computed(() => !!andalePluginsStockNotificationsPublic.userId);
    const isThemeAdaptive = computed(() => uiVariant.value === 'inherit');
    const hasBackInStockSubscription = computed(() => !!backInStockSubscription.value?.uuid);
    const isSingleBisCompact = computed(() => (
      shouldRenderBackInStock.value &&
      !shouldRenderPriceDrop.value &&
      !hasBackInStockSubscription.value
    ));
    const submitButtonClass = computed(() => (
      isThemeAdaptive.value
        ? themeSubmitButtonClasses.value
        : 'ssn-submit-button ssn-submit-button--styled'
    ));
    const canSubmit = computed(() => {
      return isEmailValid.value && (isSubscribedToBackInStock.value || isSubscribedToPriceDrop.value || isUnsubscribing.value) && !subscribing.value;
    });

    //#endregion computed properties

    const clearMessageDismissTimeout = () => {
      if (messageDismissTimeout.value) {
        window.clearTimeout(messageDismissTimeout.value);
        messageDismissTimeout.value = null;
      }
    };

    const dismissMessage = () => {
      clearMessageDismissTimeout();
      message.value = '';
    };

    const scheduleSuccessMessageDismiss = () => {
      clearMessageDismissTimeout();
      messageDismissTimeout.value = window.setTimeout(() => {
        dismissMessage();
      }, SUCCESS_MESSAGE_AUTO_DISMISS_MS);
    };

    //#region watchers

    watch(() => productId.value, (newProductId) => {
      if (newProductId) {
        // Reset subscription status when product changes
        subscribtionStatus.backInStock = getEmptyBackInStockStatus();
        subscribtionStatus.priceDrop = getEmptyPriceDropStatus();
        dismissMessage(); // Clear any previous messages
        fetchStatuses();
      }
    });

    watch(() => [message.value, messageType.value], ([newMessage, newType]) => {
      if (newMessage && newType === 'success') {
        scheduleSuccessMessageDismiss();
      } else {
        clearMessageDismissTimeout();
      }
    });

    watch(
      () => props.initialUiVariant,
      (nextVariant) => {
        uiVariant.value = nextVariant || 'inherit';
        nextTick(() => {
          hydrateThemeSubmitButtonClasses();
        });
      },
      { immediate: true }
    );

    watch(
      () => props.initialUiLayout,
      (nextLayout) => {
        uiLayout.value = nextLayout || 'stacked';
      },
      { immediate: true }
    );

    //#endregion watchers

    const syncCurrentVariationFromDom = () => {
      const form = jQuery('.variations_form');
      const variationIdValue = Number(form.find('input[name="variation_id"]').val() || 0);
      if (!variationIdValue) {
        return;
      }

      const variations = form.data('product_variations');
      if (!Array.isArray(variations)) {
        productId.value = variationIdValue;
        return;
      }

      const currentVariation = variations.find((variation) => Number(variation?.variation_id) === variationIdValue);
      if (!currentVariation) {
        productId.value = variationIdValue;
        return;
      }

      productId.value = variationIdValue;
      isProductOutOfStock.value = currentVariation.is_in_stock === false;
    };

    const listenToVariationChange = () => {
      const form = jQuery('.variations_form');
      form.on('found_variation', (_event, variation) => {
        if (variation?.variation_id) {
          // Update the productId to the variation ID
          // This will trigger the reactivity system to update the form
          // and fetch the new subscription status for the variation.
          productId.value = variation.variation_id;
          isProductOutOfStock.value = variation.is_in_stock === false;

        }
      });
      form.on('reset_data', () => {
        productId.value = props.initialProductId;
        isProductOutOfStock.value = props.initialProductIsOutOfStock;
      });

      syncCurrentVariationFromDom();
    };

    const removeVariationListeners = () => {
      const form = jQuery('.variations_form');
      form.off('found_variation');
      form.off('reset_data');
    };

    const hydrateThemeSubmitButtonClasses = () => {
      if (!isThemeAdaptive.value || !(formElement.value instanceof HTMLElement)) {
        themeSubmitButtonClasses.value = [];
        return;
      }

      const themeButton = formElement.value
        .closest('.product, .type-product, .summary, .entry-summary')
        ?.querySelector('.single_add_to_cart_button, .single_add_to_cart_button.button, .button, button');

      if (!(themeButton instanceof HTMLElement)) {
        themeSubmitButtonClasses.value = [];
        return;
      }

      themeSubmitButtonClasses.value = Array.from(themeButton.classList)
        .filter((className) => className && !className.startsWith('ssn-') && className !== 'disabled');
    };


    const fetchStatuses = async () => {
      const postData = new URLSearchParams({
        nonce: andale.nonce,
        action: 'smart_stock_notifications_get_subscription_statuses',
        product_id: productId.value,
        email: email.value,
      });

      try {
        const res = await fetch(andale.ajaxUrl, {
          method: 'POST',
          body: postData
        }).then(r => r.json());

        if (res.success && res.data) {
          const { back_in_stock: backInStock, price_drop: priceDrop } = res.data;
          const backInStockEmail = isBackInStockEnabled.value ? backInStock?.email : null;
          const priceDropEmail = isPriceDropEnabled.value ? priceDrop?.email : null;
          const signedUpEmail = backInStockEmail || priceDropEmail;
          if (!(isLoggedIn.value) && signedUpEmail) {
            email.value = signedUpEmail;
          }

          updateSubscriptionStatus('backInStock', backInStock, shouldRenderBackInStock.value);
          updateSubscriptionStatus('priceDrop', priceDrop, shouldRenderPriceDrop.value);
          syncOriginalSnapshot();

          // // Back-in-stock subscription
          // subscribtionStatus.backInStock.uuid = isBackInStockEnabled.value ? (backInStock?.uuid || null) : null;
          // subscribtionStatus.backInStock.subscribed = backInStock?.uuid ? !!backInStock?.subscribed : true;

          // // Price drop subscription
          // subscribtionStatus.priceDrop.uuid = isPriceDropEnabled.value ? (priceDrop?.uuid || null) : null;
          // subscribtionStatus.priceDrop.subscribed = isPriceDropEnabled.value ? (priceDrop?.uuid ? !!priceDrop?.subscribed : true) : false;
          // subscribtionStatus.priceDrop.targetPrice = isPriceDropEnabled.value ? (priceDrop?.subscription?.target_price || null) : null;
        }
      } catch (e) {
        console.error('Status fetch failed:', e);
      }
    };
    //#region helper functions

    const updateSubscriptionStatus = (type, statusData, isEnabled) => {
      const state = subscribtionStatus[type];

      if (!isEnabled) {
        state.uuid = null;
        state.subscribed = false;
        if (type === 'priceDrop') {
          state.targetPrice = null;
        }
        return;
      }

      const isSubscribed = statusData?.uuid ? !!statusData?.subscribed : (type === 'backInStock' ? true : false);
      state.uuid = statusData?.uuid || null;
      state.subscribed = isSubscribed;

      if (type === 'priceDrop') {
        state.targetPrice = statusData?.formatted_target_price || statusData?.display_target_price || statusData?.target_price || null;
      }
    };

    const syncOriginalSnapshot = () => {
      originalSubscriptionSnapshot.backInStock.uuid = subscribtionStatus.backInStock.uuid;
      originalSubscriptionSnapshot.backInStock.subscribed = subscribtionStatus.backInStock.subscribed;
      originalSubscriptionSnapshot.priceDrop.uuid = subscribtionStatus.priceDrop.uuid;
      originalSubscriptionSnapshot.priceDrop.subscribed = subscribtionStatus.priceDrop.subscribed;
      originalSubscriptionSnapshot.priceDrop.targetPrice = subscribtionStatus.priceDrop.targetPrice;
    };

    const buildSubmissionIntent = () => {
      const hadBackInStock = !!originalSubscriptionSnapshot.backInStock?.uuid;
      const hadPriceDrop = !!originalSubscriptionSnapshot.priceDrop?.uuid;
      const nextBackInStock = shouldRenderBackInStock.value ? !!isSubscribedToBackInStock.value : false;
      const nextPriceDrop = shouldRenderPriceDrop.value ? !!isSubscribedToPriceDrop.value : false;
      const previousTargetPrice = normalizeComparableTargetPrice(
        originalSubscriptionSnapshot.priceDrop?.targetPrice
      );
      const nextTargetPrice = normalizeComparableTargetPrice(
        priceDropSubscription.value?.targetPrice
      );

      const added =
        (!hadBackInStock && nextBackInStock) ||
        (!hadPriceDrop && nextPriceDrop);

      const removed =
        (hadBackInStock && !nextBackInStock) ||
        (hadPriceDrop && !nextPriceDrop);

      const updated =
        hadPriceDrop &&
        nextPriceDrop &&
        previousTargetPrice !== nextTargetPrice;

      return {
        added,
        removed,
        updated,
      };
    };

    const getSuccessMessageForIntent = (intent) => {
      if (intent.added && !intent.removed && !intent.updated) {
        return localizations.youHaveSuccessfullySignedUp;
      }

      if (intent.removed && !intent.added && !intent.updated) {
        return localizations.youHaveSuccessfullyUnsubscribed || localizations.youHaveSuccessfullySignedUp;
      }

      return localizations.yourNotificationPreferencesHaveBeenUpdated || localizations.youHaveSuccessfullySignedUp;
    };

    const applySignupResponse = (response) => {
      if (!response || typeof response !== 'object') {
        return;
      }

      const backInStockData = response?.back_in_stock?.data || null;
      const priceDropData = response?.price_drop?.data || null;

      if (shouldRenderBackInStock.value) {
        if (response?.back_in_stock?.success === true && backInStockData?.uuid) {
          updateSubscriptionStatus('backInStock', {
            ...backInStockData,
            subscribed: true,
          }, true);
        } else if (response?.back_in_stock?.success === true) {
          updateSubscriptionStatus('backInStock', null, true);
        }
      }

      if (shouldRenderPriceDrop.value) {
        if (response?.price_drop?.success === true && priceDropData?.uuid) {
          updateSubscriptionStatus('priceDrop', {
            ...priceDropData,
            subscribed: true,
          }, true);
        } else if (response?.price_drop?.success === true) {
          updateSubscriptionStatus('priceDrop', null, true);
        }
      }

      syncOriginalSnapshot();
    };

    const applyPreviewSubscriptionState = () => {
      if (!props.previewMode || !props.previewSubscriptionState) {
        return;
      }

      const previewBackInStock = props.previewSubscriptionState.backInStock || {};
      const previewPriceDrop = props.previewSubscriptionState.priceDrop || {};

      subscribtionStatus.backInStock.uuid = shouldRenderBackInStock.value ? (previewBackInStock.uuid || null) : null;
      subscribtionStatus.backInStock.subscribed = shouldRenderBackInStock.value ? !!previewBackInStock.subscribed : false;

      subscribtionStatus.priceDrop.uuid = shouldRenderPriceDrop.value ? (previewPriceDrop.uuid || null) : null;
      subscribtionStatus.priceDrop.subscribed = shouldRenderPriceDrop.value ? !!previewPriceDrop.subscribed : false;
      subscribtionStatus.priceDrop.targetPrice = shouldRenderPriceDrop.value ? (previewPriceDrop.targetPrice || '') : null;
      syncOriginalSnapshot();
    };

    //#endregion helper functions

    const doSignup = async () => {
      message.value = 'Processing...';
      messageType.value = 'primary';
      subscribing.value = true;
      const submissionIntent = buildSubmissionIntent();
      // Execute reCAPTCHA and get the token
      if (isRecaptchaEnabled) {
        recaptchaToken.value = await executeRecaptcha('submit');
        if (!recaptchaToken.value) {
          alert("Failed to verify reCAPTCHA. Please try again.");
          return;
        }
      }
      const postData = new URLSearchParams({
        nonce: andale.nonce,
        action: 'smart_stock_notifications_signup_multiple_types',
        product_id: productId.value,
        email: email.value,
        recaptcha_token: recaptchaToken.value, // Or your reCAPTCHA token
        // Namespaced keys
      });

      if (shouldRenderBackInStock.value) {
        postData.append('back_in_stock[enabled]', isSubscribedToBackInStock.value ? '1' : '0');
      }

      if (shouldRenderPriceDrop.value) {
        postData.append('price_drop[enabled]', isSubscribedToPriceDrop.value ? '1' : '0');
        postData.append('price_drop[target_price]', priceDropSubscription.value.targetPrice || '');
      }

      try {
        const res = await fetch(andale.ajaxUrl, {
          method: 'POST',
          body: postData,
        }).then(r => r.json());

        if (res.success) {
          applySignupResponse(res.data);
          message.value = getSuccessMessageForIntent(submissionIntent);
          messageType.value = 'success';
        } else {
          message.value = res?.data?.message || localizations.anErrorOccurred;
          messageType.value = 'danger';
        }
      } catch (e) {
        console.error('Signup failed', e);
        message.value = localizations.anErrorOccurred;
        messageType.value = 'danger';
      }
      finally {
        subscribing.value = false;
        nextTick(() => {
          initFromCookieStatus(); // Reinitialize status from cookie after signup
        });
      }
    };

    const initFromCookieStatus = () => {
      // if (isLoggedIn.value) {
      //   return;
      // };

      try {
        const cookieValue = document.cookie
          .split('; ')
          .find(row => row.startsWith('apsn_subscription_statuses='));

        if (!cookieValue) {
          return;
        };

        const cookieJson = decodeURIComponent(cookieValue.split('=')[1]);
        const parsed = JSON.parse(cookieJson);
        const productKey = String(productId.value); // productId is a Number, keys are Strings

        const productStatus = parsed[productKey];
        // if (!productStatus) {
        //   return;
        // };

        if (!shouldRenderBackInStock.value) {
          delete parsed[productKey]?.BACK_IN_STOCK;
        }
        if (!shouldRenderPriceDrop.value) {
          delete parsed[productKey]?.PRICE_DROP;
        }
        const emailFromBackInStock = shouldRenderBackInStock.value ? (parsed[productKey]?.BACK_IN_STOCK?.email || null) : null;
        const emailFromPriceDrop = shouldRenderPriceDrop.value ? (parsed[productKey]?.PRICE_DROP?.email || null) : null;

        // if (emailFromPriceDrop || emailFromBackInStock) {
        //   email.value = emailFromPriceDrop || emailFromBackInStock || '';
        // }
        if (!(isLoggedIn.value) && (emailFromBackInStock || emailFromPriceDrop)) {
          email.value = emailFromBackInStock || emailFromPriceDrop;
        }

        subscribtionStatus.backInStock.uuid = shouldRenderBackInStock.value ? (productStatus?.BACK_IN_STOCK?.uuid ?? null) : null;
        subscribtionStatus.backInStock.subscribed = productStatus?.BACK_IN_STOCK?.uuid ? true : false;

        // Price Drop
        const priceDropUuid = productStatus?.PRICE_DROP?.uuid || null;
        subscribtionStatus.priceDrop.uuid = shouldRenderPriceDrop.value ? priceDropUuid : null;
        subscribtionStatus.priceDrop.subscribed = shouldRenderPriceDrop.value ? !!priceDropUuid : false;
        const cookieTargetPrice = productStatus?.PRICE_DROP?.formatted_target_price
          || productStatus?.PRICE_DROP?.display_target_price
          || (
            productStatus?.PRICE_DROP?.target_price !== undefined && productStatus?.PRICE_DROP?.target_price !== null
              ? formatLocalizedNumber(productStatus?.PRICE_DROP?.target_price, true)
              : null
          );
        subscribtionStatus.priceDrop.targetPrice = shouldRenderPriceDrop.value
          ? (cookieTargetPrice ?? productStatus?.PRICE_DROP?.subscription?.target_price ?? null)
          : null;
        syncOriginalSnapshot();
      } catch (err) {
        console.warn('Failed to parse apsn_subscription_statuses cookie:', err);
      }
    };



    onMounted(() => {
      hydrateThemeSubmitButtonClasses();
      if (props.previewMode) {
        applyPreviewSubscriptionState();
        return;
      }

      listenToVariationChange();
      initFromCookieStatus();
      fetchStatuses();
    });

    onBeforeUnmount(() => {
      if (!props.previewMode) {
        removeVariationListeners();
      }
    });

    watch(
      () => props.previewSubscriptionState,
      () => {
        if (props.previewMode) {
          applyPreviewSubscriptionState();
        }
      },
      { deep: true, immediate: true }
    );

    onBeforeUnmount(() => {
      clearMessageDismissTimeout();
    });

    return {
      email,
      productId,
      isProductOutOfStock,
      shouldRender,
      isBackInStockEnabled,
      isBackInStockAutoInject,
      shouldRenderBackInStock,
      isPriceDropEnabled,
      isPriceDropAutoInject,
      shouldRenderPriceDrop,
      targetPrice,
      subscribing,
      subscribtionStatus,
      backInStockSubscription,
      isSubscribedToBackInStock,
      priceDropSubscription,
      isSubscribedToPriceDrop,
      message,
      messageType,
      dismissMessage,
      isLoggedIn,
      isThemeAdaptive,
      isSingleBisCompact,
      submitButtonClass,
      formInlineStyle,
      formElement,
      uiVariant,
      uiLayout,
      canSubmit,
      doSignup,
      localizations,
      recaptchaToken,
      recaptchaError,
    };
  }
};
</script>
<style scoped>
.andale-smart-stock-notifications-signup-form {
  background-color: var(--ssn-form-background, #ffffff);
  color: var(--ssn-form-text-color, inherit);
  border-radius: var(--ssn-form-radius, 8px);
  padding: 1.2rem 0.8rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border: var(--ssn-form-stroke, var(--ssn-form-border, 1px solid #e6e6e6));
  margin-top: 1em;
  margin-bottom: 15px;
  max-width: var(--ssn-form-max-width, 520px);
}

.ssn-signup-form--default {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.ssn-signup-form--minimal {
  border: 0;
  box-shadow: none;
  padding: 0.4rem 0.45rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}

.ssn-signup-form--minimal .signup-options,
.ssn-signup-form--minimal .andale-smart-stock-notifications-a-subscription-option {
  padding-left: 0;
  padding-right: 0;
}

.ssn-signup-form--minimal .signup-options {
  gap: 0.2rem;
  margin-top: 0.35rem;
}

.ssn-signup-form--minimal .ssn-signup-form-title {
  font-size: 1.05rem;
}

.ssn-signup-form--minimal .andale-smart-stock-notifications-signup-cta {
  justify-content: flex-end;
  margin-top: 0.25rem;
}

.ssn-signup-form--card {
  border: 1px solid #d7e1ec;
  border-radius: 16px;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
  padding: 1.35rem 1rem;
}

.ssn-signup-form--inline {
  border: 0;
  box-shadow: none;
  padding: 0.9rem 0.45rem;
  max-width: none;
}

.ssn-signup-form--inline .signup-options {
  padding-left: 0;
}

.ssn-signup-form--inline .andale-smart-stock-notifications-signup-cta {
  justify-content: flex-end;
  margin-top: 0.5rem;
}

.ssn-signup-form--layout-inline {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  column-gap: 1rem;
  row-gap: 0.75rem;
  align-items: end;
}

.ssn-signup-form--layout-inline .ssn-email-field {
  grid-column: 1 / -1;
}

.ssn-signup-form--layout-inline .signup-options {
  padding-left: 0;
  margin-top: 0;
  min-width: 0;
}

.ssn-signup-form--layout-inline .andale-smart-stock-notifications-a-subscription-option {
  padding-left: 0;
  padding-right: 0;
}

.ssn-signup-form--layout-inline .andale-smart-stock-notifications-signup-cta {
  justify-content: flex-end;
  align-self: end;
  margin-top: 0;
}

.ssn-signup-form--layout-compact {
  padding: 0.9rem 0.75rem;
}

.ssn-signup-form--layout-compact .signup-options {
  gap: 0.2rem;
}

.ssn-signup-form--layout-compact .ssn-signup-form-title {
  font-size: 1.05rem;
}

.ssn-signup-form--layout-compact .ssn-text-input,
.ssn-signup-form--layout-compact .ssn-submit-button--styled {
  min-height: 40px;
}

.ssn-email-field {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.ssn-single-bis-heading {
  margin: 0 0 8px;
  font-size: var(--ssn-title-font-size, 1.2em);
  font-weight: var(--ssn-title-font-weight, 600);
  color: var(--ssn-title-color, #333333);
  line-height: 1.35;
}

.ssn-single-bis-description {
  margin: 0 0 10px;
  color: var(--ssn-help-text-color, #5f6b7a);
  line-height: 1.45;
}

.ssn-single-bis-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: min(100%, 560px);
  width: 100%;
}

.ssn-single-bis-controls [data-ssn-part="email-field"] {
  flex: 1 1 auto;
  min-width: 0;
  gap: 0;
}

.ssn-single-bis-controls [data-ssn-part="email-label"] {
  display: none !important;
}

.ssn-single-bis-controls [data-ssn-part="email-input"] {
  display: block;
  margin-top: 0;
  width: 100%;
}

.ssn-single-bis-controls [data-ssn-part="cta"] {
  flex: 0 0 auto;
  margin: 0;
  display: flex;
  align-items: stretch;
  justify-content: flex-end;
}

.ssn-single-bis-controls--logged-in [data-ssn-part="cta"] {
  margin-left: auto;
}

.ssn-field-label {
  font-weight: 600;
  color: inherit;
}

.ssn-text-input {
  width: 100%;
  padding: 0.7rem 0.85rem;
  border: var(--ssn-input-stroke, var(--ssn-input-border, 1px solid #cfd6de));
  border-radius: var(--ssn-form-radius, 8px);
  background: var(--ssn-input-background, #fff);
  color: inherit;
  line-height: 1.4;
}

.ssn-text-input:focus {
  outline: 2px solid rgba(37, 99, 235, 0.18);
  outline-offset: 1px;
  border-color: var(--ssn-input-focus-stroke-color, var(--ssn-input-focus-border-color, #2563eb));
}

@media (max-width: 480px) {
  .andale-smart-stock-notifications-signup-form {
    max-width: 100%;
  }
}

.andale-smart-stock-notifications-a-subscription-option {
  padding: 0.5rem 0.5rem;
}

.andale-smart-stock-notifications-signup-cta {
  display: flex;
  justify-content: end;
  margin-top: 4px;
}

.ssn-submit-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  vertical-align: middle;
}

.ssn-submit-button--styled {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 0.7rem 1.1rem;
  border: 0;
  border-radius: 8px;
  background: #1990d0;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}

.ssn-submit-button--styled:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.signup-options {
  /* padding-left: 14px; */
  padding-left: 9px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ssn-signup-form-title {
  font-size: 1.2em;
  font-weight: 600;
  /* margin-bottom: 1em; */
  color: var(--ssn-title-color, #333333);
}

/* Styling the notification text @todo: delete*/
.notification-text {
  /* Slightly larger text for emphasis */
  font-size: 18px;
  /* Bold text for visibility */
  font-weight: bold;
  /* Add space below the text */
  margin-bottom: 10px;
}

.compact-field ::v-deep(.v-field__input) {
  padding-top: 1.1rem;
  padding-bottom: 0.3rem;
}

.is-invalid {
  border-color: red;
}

.error-text {
  color: red;
  font-size: 12px;
}

.success {
  color: green;
}

.error {
  color: red;
}

.ssn-feedback {
  margin-top: 0.9rem;
  padding: 0.7rem 0.9rem;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.ssn-feedback--primary {
  background: #eef6ff;
  color: #1f4f7a;
}

.ssn-feedback--success {
  background: #e8f7ed;
  color: #155724;
}

.ssn-feedback--danger {
  background: #fff1f2;
  color: #9f1239;
}

.ssn-feedback__dismiss {
  border: 0;
  background: transparent;
  color: inherit;
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

@media (max-width: 640px) {
  .ssn-signup-form--layout-inline {
    display: block;
  }

  .ssn-signup-form--layout-inline .andale-smart-stock-notifications-signup-cta {
    margin-top: 0.5rem;
  }

  .ssn-single-bis-controls {
    flex-direction: column;
    align-items: stretch;
    max-width: 100%;
  }

  .ssn-single-bis-controls [data-ssn-part="email-field"],
  .ssn-single-bis-controls [data-ssn-part="cta"],
  .ssn-single-bis-controls [data-ssn-part="submit-button"] {
    width: 100%;
    max-width: 100%;
  }
}

/* Styling for the alert box */
.alert-signed-up {
  font-size: 0.9rem;
  /* Slightly smaller font size */
  padding: 8px 12px;
  /* Reduced padding */
  border-radius: 4px;
  /* Slightly rounded corners */
  margin-bottom: 8px;
  /* Reduced margin */
  display: flex;
  align-items: center;
  /* Align content vertically */
}

/* Style for the success text */
.alert-signed-up .success-text {
  flex-grow: 1;
  margin-right: 8px;
  /* Space between text and button */
}

/* Style for the unsubscribe button */
.alert-signed-up .unsubscribe-button {
  font-size: 0.85rem;
  padding: 4px 8px;
  /* Compact button size */
  background-color: #e74c3c;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.alert-signed-up .unsubscribe-button:hover {
  background-color: #c0392b;
  /* Darker red for hover */
}

.px-1 {
  padding-left: 4px;
  padding-right: 4px;
}

.mb-0 {
  margin-bottom: 0;
}
</style>
